/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import PublicSeoPage from '../../components/shared/PublicSeoPage';
import PageMeta from '../../components/shared/PageMeta';

// The privacy policy, written from what the code actually does.
//
// WHY IT EXISTS AT ALL: there was none. /privacy redirected to a login wall,
// /privacy-policy redirected to /login?redirectTo=%2F-policy, and
// /termsandconditions, /data-deletion and /delete-account all served the
// marketing homepage. docs.hevolve.ai/privacy 404s. The footer on every SEO
// page linked "Privacy Policy" and sent people to the homepage. That is a
// problem on its own, and it is a hard blocker for two app stores: Meta App
// Review requires a Privacy Policy URL and a Data Deletion URL for
// instagram_content_publish, and Google Play requires a privacy policy for a
// published app, which com.hertzai.hevolve is.
//
// EVERY CLAIM BELOW WAS CHECKED IN THE CODE, not assumed:
//   - Google Analytics G-E527GVJ64Y, live in public/index.html:322 and
//     confirmed present in the served HTML.
//   - Sentry crash reporting, src/hooks/useCrashReporter.js:33.
//   - The localStorage keys are the ones the app actually writes.
//   - Marketing attribution stores a truncated SHA-256 of IP and user agent,
//     never the raw values (HARTOS _record_marketing_event).
//   - There is NO account deletion endpoint. Deletion is a manual request, and
//     this says so rather than implying a self-serve button that does not
//     exist.
//
// A LAWYER HAS NOT REVIEWED THIS. It is an accurate description of system
// behaviour by the people who can read the system, which is a far better
// starting point than nothing, and it should be reviewed before it is relied
// on in a dispute. Where behaviour changes, change this with it.

const UPDATED = '19 August 2026';

const SECTIONS = [
  {
    h: 'The short version',
    p: [
      'Nunba runs AI models on your own computer. When you use the desktop app, what you type and the files you point it at stay on your machine, because there is nowhere else for them to go. You can watch the network and check.',
      'This website is a different thing from the app, and it does collect some data, which is what the rest of this page is about.',
    ],
  },
  {
    h: 'What the desktop app does with your data',
    p: [
      'The model runs locally. Conversations, documents and images you give it are processed on your device and are not sent to us.',
      'Some features reach the network by their nature: checking for updates, downloading a model you asked for, and any channel you deliberately connect (for example Telegram or Slack) which sends your messages to that service under that service\'s own terms.',
      'If you opt in to the federated hive, what is shared is model improvements rather than your content. Opting in is a choice you make; it is not on by default.',
    ],
  },
  {
    h: 'What this website collects',
    p: [
      'Analytics. We use Google Analytics (measurement ID G-E527GVJ64Y) to count visits and see which pages people read. Google sets its own cookies and processes that data under its terms.',
      'Crash reports. We use Sentry to receive errors the site throws, so we can fix them. A crash report can include the page you were on and a technical stack trace.',
      'Attribution. If you arrive through a tagged link we record which channel it was, along with the event (a click, a download) and a truncated one-way hash of your IP address and browser string. We store the hashes, not the values, so the count is possible and identifying you from it is not.',
      'Things the site remembers in your browser. Signing in stores a token and an account id. Using it as a guest stores a guest id and a display name. Preferences such as your chosen agent, language and appearance are stored the same way. All of that lives in your browser\'s local storage and clearing your browser data removes it.',
    ],
  },
  {
    h: 'What we do not do',
    p: [
      'We do not sell your data.',
      'We do not use your conversations to train models we then ship to other people.',
      'We do not run advertising trackers beyond the analytics named above.',
    ],
  },
  {
    h: 'Accounts, and payments',
    p: [
      'If you create an account we store what you gave us: an email address, a display name, and the content you choose to post to the community surfaces.',
      'Payments, where they happen, run through Stripe and PhonePe. Card details go to them, not to us; we never see or store a card number.',
    ],
  },
  {
    h: 'Deleting your data',
    p: [
      'There is currently no self-serve delete button, and we would rather say that than imply one exists. Deletion is handled as a request by a person.',
      'Write to privacy@hevolve.ai from the address on the account and ask for deletion. The instructions, and what happens to each kind of data, are set out on the data deletion page.',
    ],
    link: { to: '/data-deletion', label: 'How to request deletion' },
  },
  {
    h: 'Children',
    p: [
      'The Kids Learning surfaces are designed to be used with a parent or guardian. We do not knowingly collect personal data from a child beyond what the account holder provides. If you believe we have, write to privacy@hevolve.ai and we will remove it.',
    ],
  },
  {
    h: 'Changes, and how to reach us',
    p: [
      'When this changes, the date at the top changes with it, and the change lands in the same public git history as the rest of the site.',
      'Questions, corrections and requests: privacy@hevolve.ai.',
    ],
  },
];

export default function PrivacyPolicyPage() {
  return (
    <>
      <PageMeta
        title="Privacy Policy | Hevolve AI"
        description="What Hevolve AI and Nunba do with your data. The model runs on your machine; this page sets out what the website collects, what it does not, and how to have your data deleted."
        ogTitle="Privacy Policy | Hevolve AI"
        ogDescription="The model runs on your machine. Here is exactly what the website collects and how to have your data deleted."
        path="/legal/privacy"
      >
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Privacy Policy',
          url: 'https://hevolve.ai/legal/privacy',
          description: 'What Hevolve AI and Nunba do with your data, and how to have it deleted.',
          publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' },
        })}</script>
      </PageMeta>

      <PublicSeoPage
        heading="Privacy Policy"
        headingVariant="h4"
        maxWidth="md"
        subheading={`Last updated ${UPDATED}. Written from what the software actually does, in plain language, because a policy nobody can read protects nobody.`}
      >
        {SECTIONS.map((s) => (
          <Box key={s.h} sx={{ mb: 4 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1.25 }}>{s.h}</Typography>
            {s.p.map((para, i) => (
              <Typography key={i} variant="body1"
                sx={{ color: 'rgba(255,255,255,0.78)', lineHeight: 1.85, mb: 1.5 }}>
                {para}
              </Typography>
            ))}
            {s.link && (
              <Typography component={Link} to={s.link.to}
                sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#17c07f', textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                {s.link.label} &rarr;
              </Typography>
            )}
          </Box>
        ))}

        <Box sx={{ mt: 5, p: 2.5, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.10)' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', lineHeight: 1.7 }}>
            Every statement here describes behaviour that is in the source code, and the source is public. If you
            find something on this page that the software does not do, that is a bug in the page and we want to
            know: privacy@hevolve.ai.
          </Typography>
        </Box>
      </PublicSeoPage>
    </>
  );
}
